import React, { useState, useEffect } from 'react';
import { getStoredToken } from '../utils/storage';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  onLoad?: () => void;
  onError?: () => void;
  [key: string]: any; // 允许其他props
}

/**
 * 带认证的图片组件
 * 自动为图片请求添加认证头
 */
export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className,
  loading = 'lazy',
  onLoad,
  onError,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        
        const token = getStoredToken();
        
        // 如果是相对路径的API图片，需要添加认证头
        if (src.startsWith('/api/images/')) {
          // 🎯 修复：使用正确的环境变量和开发环境后端URL
          const apiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000/api';
          // 移除src中的/api前缀，因为apiBaseUrl已经包含了/api
          const imagePath = src.replace('/api', '');
          const fullUrl = `${apiBaseUrl}${imagePath}`;
          
          const response = await fetch(fullUrl, {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setImageSrc(objectUrl);
        } else {
          // 对于外部URL，直接使用
          setImageSrc(src);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('图片加载失败:', error);
        setHasError(true);
        setIsLoading(false);
        onError?.();
      }
    };

    if (src) {
      loadImage();
    }

    // 清理函数，释放blob URL
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src]);

  const handleLoad = () => {
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  if (hasError) {
    return (
      <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
        <span className="text-gray-500 text-sm">图片加载失败</span>
      </div>
    );
  }

  if (isLoading || !imageSrc) {
    return (
      <div className={`bg-gray-200 animate-pulse ${className}`}>
        {/* 加载占位符 */}
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      loading={loading}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default AuthenticatedImage;